import { useCallback } from "react";

import { useTranslation } from "react-i18next";

import { FunnelIcon, XIcon } from "lucide-react";

import { Button } from "simplydo/react/ui/button";
import {
  Dropdown,
  DropdownDivider,
  DropdownItem,
  DropdownMenu,
} from "simplydo/react/ui/dropdown";
import { Icon } from "simplydo/react/ui/icon";

import actions from "actions";
import { useAppDispatch, useAppSelector } from "store";

import {
  applySavedIdeaFilters,
  clearPersistableIdeaFilters,
  countNonDefaultIdeaFilters,
} from "components/lib/Filters/filterUtils";

type ChallengeIdeaFiltersButtonProps = {
  /** When set, used instead of the default show/hide filters label. */
  label?: string;
  color?: "primary" | "secondary" | "positive" | "negative";
  variant?: "regular" | "outline" | "ghost";
  showIcon?: boolean;
};

function ChallengeIdeaFiltersButton({
  label,
  color,
  variant = "outline",
  showIcon = true,
}: ChallengeIdeaFiltersButtonProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const ideaFilters = useAppSelector((state) => state.challengeIdeaFilters);
  const user = useAppSelector((state) => state.user);
  const { filtersVisible } = ideaFilters;
  const numberOfFiltersNotDefault = countNonDefaultIdeaFilters(ideaFilters);
  const savedFilters = user?.savedChallengeIdeaFilters ?? [];

  const updateFilters = useCallback(
    (data: Parameters<typeof actions.challengeIdeaFilters.update>[0]) =>
      dispatch(actions.challengeIdeaFilters.update(data)),
    [dispatch],
  );

  const clearAll = useCallback(() => {
    updateFilters(clearPersistableIdeaFilters());
  }, [updateFilters]);

  const applyPreset = useCallback(
    (filters: (typeof savedFilters)[number]["filters"]) => {
      updateFilters(
        applySavedIdeaFilters(
          filters as Parameters<typeof applySavedIdeaFilters>[0],
        ),
      );
    },
    [updateFilters],
  );

  const buttonLabel = `${
    label ??
    (filtersVisible
      ? t("ideas.filters.hideButton")
      : t("ideas.filters.showButton"))
  }${numberOfFiltersNotDefault ? ` (${numberOfFiltersNotDefault})` : ""}`;

  const button = (
    <Button
      size="small"
      color={color}
      variant={variant}
      onClick={() => updateFilters({ filtersVisible: !filtersVisible })}
    >
      {showIcon ? <Icon icon={FunnelIcon} /> : null}
      {buttonLabel}
    </Button>
  );

  // Only show the hover dropdown when there is at least one actionable item
  // (saved presets or a clearable filter).
  const hasDropdownItems =
    savedFilters.length > 0 || numberOfFiltersNotDefault > 0;

  if (!hasDropdownItems) {
    return button;
  }

  return (
    <Dropdown on="hover" trigger={button}>
      <DropdownMenu>
        {savedFilters.length > 0 ? (
          <>
            {savedFilters.map((preset) => (
              <DropdownItem
                key={preset.id}
                onClick={() => applyPreset(preset.filters)}
              >
                {preset.name}
              </DropdownItem>
            ))}
            <DropdownDivider />
          </>
        ) : null}
        <DropdownItem
          disabled={numberOfFiltersNotDefault === 0}
          onClick={clearAll}
        >
          <Icon icon={XIcon} />
          {t("ideas.filters.saved.clearAll")}
        </DropdownItem>
      </DropdownMenu>
    </Dropdown>
  );
}

export default ChallengeIdeaFiltersButton;
